import React from 'react';
import { Form } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Button } from '@oysterjs/ui/button';
import { Textarea } from '@oysterjs/ui/textarea';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/ui/checkbox';
import { MessageRating } from '../api/graphql';

export type UseFeedbackDialogOptions<TReason extends string> = {
  onSubmit: (payload: {
    rating: MessageRating.Down;
    reasons: TReason[];
    comment?: string;
  }) => Promise<void>;
  onSubmitPositive: (payload: { rating: MessageRating.Up }) => Promise<void>;
  otherReasonValue: TReason;
  resetOnContentChange?: boolean;
  content?: unknown;
};

export const useFeedbackDialog = <TReason extends string>({
  onSubmit,
  onSubmitPositive,
  otherReasonValue,
  resetOnContentChange = false,
  content
}: UseFeedbackDialogOptions<TReason>) => {
  const [showFeedbackDialog, setShowFeedbackDialog] = React.useState(false);
  const [rating, setRating] = React.useState<MessageRating | null>(null);
  const [selectedReasons, setSelectedReasons] = React.useState<TReason[]>([]);
  const [feedbackComment, setFeedbackComment] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const handleThumbClick = async (value: MessageRating) => {
    if (isSubmitting) {
      return;
    }

    if (value === MessageRating.Up) {
      setShowFeedbackDialog(false);
      setSelectedReasons([]);
      setFeedbackComment('');
      setSubmitError(null);

      setIsSubmitting(true);
      try {
        await onSubmitPositive({ rating: MessageRating.Up });
        setRating(MessageRating.Up);
      } catch {
        setSubmitError('We could not save your feedback. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setSubmitError(null);
    setShowFeedbackDialog(true);
  };

  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedComment = feedbackComment.trim();

    // Validate that if OTHER is selected, comment must be provided
    if (selectedReasons.includes(otherReasonValue) && !trimmedComment) {
      setSubmitError('Please provide a comment when selecting "Other reason"');
      return;
    }

    // Allow submission if there's either a comment or reasons (or both)
    if (selectedReasons.length === 0 && !trimmedComment) {
      setSubmitError('Please select at least one reason or provide a comment');
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({
        rating: MessageRating.Down,
        reasons: selectedReasons,
        ...(trimmedComment ? { comment: trimmedComment } : {})
      });

      setRating(MessageRating.Down);
      setShowFeedbackDialog(false);
      setSelectedReasons([]);
      setFeedbackComment('');
    } catch {
      setSubmitError('We could not save your feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFeedbackCancel = () => {
    if (isSubmitting) {
      return;
    }

    setShowFeedbackDialog(false);
    setSelectedReasons([]);
    setFeedbackComment('');
    setSubmitError(null);
  };

  const toggleReason = (reason: TReason) => {
    if (submitError) {
      setSubmitError(null);
    }

    setSelectedReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  const isSubmitDisabled =
    !feedbackComment.trim() &&
    (selectedReasons.length === 0 || selectedReasons.includes(otherReasonValue));

  // Reset state when content changes if requested
  React.useEffect(() => {
    if (resetOnContentChange) {
      setRating(null);
      setSelectedReasons([]);
      setFeedbackComment('');
      setShowFeedbackDialog(false);
      setSubmitError(null);
      setIsSubmitting(false);
    }
  }, [resetOnContentChange, content]);

  return {
    showFeedbackDialog,
    rating,
    selectedReasons,
    feedbackComment,
    isSubmitting,
    submitError,
    handleThumbClick,
    handleFeedbackSubmit,
    handleFeedbackCancel,
    toggleReason,
    isSubmitDisabled,
    setFeedbackComment,
    setSubmitError
  };
};

export type FeedbackDialogProps<TReason extends string> = {
  open: boolean;
  onClose: () => void;
  reasons: Record<TReason, string>;
  selectedReasons: TReason[];
  feedbackComment: string;
  isSubmitting: boolean;
  submitError: string | null;
  isSubmitDisabled: boolean;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  onToggleReason: (reason: TReason) => void;
  onCommentChange: (value: string) => void;
  onClearError: () => void;
  title?: string;
  description?: string;
  commentPlaceholder?: string;
  commentRows?: number;
};

export const FeedbackDialog = <TReason extends string>({
  open,
  onClose,
  reasons,
  selectedReasons,
  feedbackComment,
  isSubmitting,
  submitError,
  isSubmitDisabled,
  onSubmit,
  onCancel,
  onToggleReason,
  onCommentChange,
  onClearError,
  title = 'Provide feedback',
  description = 'Help us improve by sharing what went wrong',
  commentPlaceholder = 'Please describe the issue',
  commentRows = 3
}: FeedbackDialogProps<TReason>) => {
  const normalizedReasons = Object.entries(reasons).map(([value, label]) => ({
    value,
    label: label as string
  }));

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>{description}</DialogDescription>
      <Form onSubmit={onSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>What went wrong?</Label>
                <Description>Select all that apply</Description>
                <div className="mt-4 max-h-96 overflow-y-auto">
                  <CheckboxGroup className="space-y-3">
                    {normalizedReasons.map((reason) => (
                      <CheckboxField key={reason.value}>
                        <Checkbox
                          color="sky"
                          checked={selectedReasons.includes(reason.value as TReason)}
                          onChange={() => {
                            if (submitError) {
                              onClearError();
                            }
                            onToggleReason(reason.value as TReason);
                          }}
                        />
                        <Label>{reason.label}</Label>
                      </CheckboxField>
                    ))}
                  </CheckboxGroup>
                </div>
              </Field>
              <Field className="mt-6">
                <Label>Additional details</Label>
                <Description>Please describe the issue</Description>
                <div className="mt-4">
                  <Textarea
                    value={feedbackComment}
                    onChange={(e) => {
                      if (submitError) {
                        onClearError();
                      }
                      onCommentChange(e.currentTarget.value);
                    }}
                    rows={commentRows}
                    placeholder={commentPlaceholder}
                    className="text-sm"
                  />
                </div>
              </Field>
              {submitError && (
                <Field className="mt-4">
                  <ErrorMessage>{submitError}</ErrorMessage>
                </Field>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="sky"
            disabled={isSubmitDisabled || isSubmitting}
            loading={isSubmitting}
          >
            Submit
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
